export type DownloadTarget = { platform: "windows" | "macos"; architecture: "x64" | "arm64" };
export type DownloadDetection = DownloadTarget | "macos-unknown" | "unsupported";
type BrowserInfo = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  userAgentData?: {
    platform?: string;
    mobile?: boolean;
    getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
  };
};

export async function detectDownloadTarget(browser: BrowserInfo, macRenderer?: () => string): Promise<DownloadDetection> {
  const { userAgent: ua, userAgentData: hints } = browser;
  // iPad desktop mode also reports MacIntel. Never hand it a desktop installer.
  if (hints?.mobile || /Android|iPhone|iPad|iPod/i.test(ua) || (/Mac/i.test(browser.platform) && browser.maxTouchPoints > 1)) return "unsupported";
  const platform = hints?.platform || browser.platform || ua;
  if (/Windows|Win32|Win64/i.test(platform)) return { platform: "windows", architecture: "x64" };
  if (!/Mac/i.test(platform)) return "unsupported";

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const detail = await Promise.race([
      hints?.getHighEntropyValues?.(["architecture"]),
      new Promise<undefined>(resolve => { timer = setTimeout(() => resolve(undefined), 1000); }),
    ]);
    if (detail?.architecture === "arm" || detail?.architecture === "arm64") return { platform: "macos", architecture: "arm64" };
    if (detail?.architecture === "x86" || detail?.architecture === "x64") return { platform: "macos", architecture: "x64" };
  } catch {
    // Client hints can be blocked by browser privacy settings.
  } finally {
    clearTimeout(timer);
  }
  try {
    const renderer = macRenderer?.() || "";
    if (/Apple M\d/i.test(renderer)) return { platform: "macos", architecture: "arm64" };
    if (/Intel|AMD|Radeon|NVIDIA/i.test(renderer)) return { platform: "macos", architecture: "x64" };
  } catch {
    // A generic Apple GPU string does not identify the CPU architecture.
  }
  return "macos-unknown";
}

export function readMacRenderer() {
  const gl = document.createElement("canvas").getContext("webgl");
  if (!gl) return "";
  try {
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    return info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : "";
  } finally {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}
